import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion'
import { useEffect, useRef } from 'react'
import { useT } from '../i18n/LocaleContext'

function Counter({ to, suffix }: { to: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  const count = useMotionValue(0)
  const rounded = useTransform(count, (v) => Math.round(v).toString())

  useEffect(() => {
    if (inView) {
      const controls = animate(count, to, { duration: 1.6, ease: [0.22, 1, 0.36, 1] })
      return controls.stop
    }
  }, [inView, to, count])

  return (
    <span ref={ref} className="inline-flex items-baseline">
      <motion.span>{rounded}</motion.span>
      <span>{suffix}</span>
    </span>
  )
}

export default function Stats() {
  const { t } = useT()

  const stats = [
    { value: 24, suffix: 'h', label: t('stats.shippingLabel'), sub: t('stats.shippingSub') },
    { value: 30, suffix: '+', label: t('stats.carriersLabel'), sub: t('stats.carriersSub') },
    { value: 100, suffix: '+', label: t('stats.platformsLabel'), sub: t('stats.platformsSub') },
    { value: 100, suffix: '%', label: t('stats.savingsLabel'), sub: t('stats.savingsSub') },
  ]

  return (
    <section className="relative py-24 bg-ink-950 text-white overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 bg-grid-dark [background-size:40px_40px] opacity-30 [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_80%)]"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[400px] bg-[radial-gradient(ellipse_at_center,rgba(59,93,255,0.25),transparent_70%)]"
      />

      <div className="relative max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-white/50">{t('stats.eyebrow')}</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-[-0.02em] text-gradient-invert">
            {t('stats.headline')}
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="bg-ink-950 p-6 sm:p-8"
            >
              <div className="text-5xl sm:text-6xl font-semibold tracking-[-0.03em] text-white">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-3 text-sm font-medium text-white">{s.label}</p>
              <p className="mt-1 text-xs text-white/50">{s.sub}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
